/**
 * Error capture (Sentry-style). Hooks global `error` / `unhandledrejection`, plus a
 * manual `captureException()` API. Errors are masked client-side and POSTed to the
 * collector's /api/errors/ingest endpoint. Low-volume, so sent immediately (keepalive)
 * with a short dedupe window to avoid storms.
 */
import { maskText } from '@aris/request-watcher-shared';
import type { ResolvedConfig } from './config.js';
import { detectSource } from './source.js';
import { uuid } from './util.js';

const SDK = { name: 'sdk-react', version: '0.1.0' };

interface ErrorPayload {
  id: string;
  project_id: string;
  environment: string;
  level: 'error' | 'warning' | 'fatal' | 'info';
  type: string;
  message: string;
  stacktrace: string | null;
  culprit: string | null;
  release: string | null;
  url: string | null;
  user_id: string | null;
  session_id: string | null;
  tags: Record<string, unknown> | null;
  timestamp: string;
}

function errorsUrl(collectorUrl: string): string {
  // .../api/events/ingest -> .../api/errors/ingest ; or append if a bare base.
  if (/\/api\/events\/ingest\/?$/.test(collectorUrl)) {
    return collectorUrl.replace(/\/api\/events\/ingest\/?$/, '/api/errors/ingest');
  }
  return `${collectorUrl.replace(/\/$/, '')}/api/errors/ingest`;
}

export function installErrorCapture(cfg: ResolvedConfig): () => void {
  if (typeof window === 'undefined') return () => {};

  const url = errorsUrl(cfg.collectorUrl);
  const recent = new Map<string, number>(); // dedupe key -> last sent ms (perf clock)

  const resolveUser = (): string | null => {
    const u = cfg.userId;
    return (typeof u === 'function' ? u() : u) ?? null;
  };

  const build = (
    err: unknown,
    level: ErrorPayload['level'],
    extraStack?: string,
  ): ErrorPayload | null => {
    const e = err as Error | undefined;
    const type = e?.name || (typeof err === 'string' ? 'Error' : 'UnknownError');
    const message = e?.message ?? String(err ?? 'unknown');
    const stack = e?.stack ?? extraStack;
    const { source } = detectSource(stack);
    const culprit = source.file ? `${source.file}${source.function ? `:${source.function}` : ''}` : null;

    return {
      id: uuid(),
      project_id: cfg.projectId,
      environment: cfg.environment,
      level,
      type,
      message: maskText(message, { maskPii: cfg.maskPii }) ?? message,
      stacktrace: maskText(stack ?? null, { maskPii: cfg.maskPii }),
      culprit,
      release: (cfg.tags.release as string) ?? null,
      url: typeof location !== 'undefined' ? location.href : null,
      user_id: resolveUser(),
      session_id: cfg.sessionId ?? null,
      tags: cfg.tags,
      timestamp: new Date().toISOString(),
    };
  };

  const send = (p: ErrorPayload | null) => {
    if (!p) return;
    const key = `${p.type}:${p.message}`.slice(0, 200);
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    const last = recent.get(key);
    if (last && now - last < 10_000) return; // dedupe within 10s
    recent.set(key, now);

    try {
      void fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: { 'content-type': 'application/json', 'x-aris-api-key': cfg.apiKey },
        body: JSON.stringify({ sdk: SDK, events: [p] }),
      }).catch(() => {});
    } catch {
      /* never throw from the error path */
    }
  };

  const onError = (ev: ErrorEvent) => send(build(ev.error ?? ev.message, 'error'));
  const onRejection = (ev: PromiseRejectionEvent) => send(build(ev.reason, 'error'));

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  // Expose manual capture for app code.
  manualCapture = (err: unknown, opts?: { level?: ErrorPayload['level'] }) =>
    send(build(err, opts?.level ?? 'error', new Error().stack));

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
    manualCapture = null;
  };
}

let manualCapture: ((err: unknown, opts?: { level?: 'error' | 'warning' | 'fatal' | 'info' }) => void) | null = null;

/** Manually report a caught error: `import { captureException } from '@aris/request-watcher-react'`. */
export function captureException(err: unknown, opts?: { level?: 'error' | 'warning' | 'fatal' | 'info' }): void {
  manualCapture?.(err, opts);
}
